/**
 * 탭 카탈로그 SSoT (architecture-review §5 P2) — Phaser 의존 0의 순수 모듈.
 *
 * 신규 탭 추가 = **여기 레코드 1줄** (구: tabVisibility TabId 유니온+TAB_ORDER 배열 +
 * BottomTabView TAB_ROWS+TAB_ICON 의 2핫파일 4개소 편집 — 멀티세션 동시 편집 충돌 지점이었다).
 *
 * - 키 선언 순서 = TAB_ORDER (SingleSelection 구성 순서 — 화면 배치와 별개).
 * - bar.row/col = 하단 탭바 표시 배치 (BottomTabView가 deriveTabRows()로 소비).
 * - icon = 탭 아이콘 텍스처 키 (THEME_IMAGE_KEYS 등재 필수 — tabCatalog.test가 정합 검증).
 * - feature = featureFlags 게이트 키 (없으면 상시 탭). 서브탭 게이트(dailyShop/moduleFusion)는
 *   최상위 탭 게이트가 아니므로 여기 달지 않는다 (ShopContainerTabView 등 컨테이너가 제어).
 * - 챕터/월드 해금 값은 `data/systemUnlocks.tsv` → `data/systemUnlockData.ts`가 SSoT.
 *
 * 탭 배치를 TSV로 빼지 않는 이유: generated JSON import는 리터럴 widening으로 TabId 유니온이
 * string으로 풀려 typed 분기·grep 추적성이 죽는다 (§5 역제안 1 기각 사유).
 */
import type { FeatureKey } from "../env/featureFlags.js";

export interface TabCatalogEntry {
  /** 하단 탭바 배치 — row 오름차순으로 행 구성, 행 내에서는 col 오름차순. */
  readonly bar: { readonly row: number; readonly col: number };
  /** 탭 아이콘 텍스처 키. 전용 아이콘 미준비 시 임시 키 재사용 후 키만 교체. */
  readonly icon: string;
  /** featureFlags 게이트 — 지정 시 해당 플래그 false면 탭 제거 (deriveEnabledTabSet). */
  readonly feature?: FeatureKey;
}

const CATALOG = {
  battle: { bar: { row: 1, col: 0 }, icon: "icon_tab_battle" },
  dungeon: { bar: { row: 1, col: 5 }, icon: "icon_tab_dungeon", feature: "dungeon" },
  statUpgrade: { bar: { row: 1, col: 1 }, icon: "icon_tab_stat" },
  spaceship: { bar: { row: 1, col: 2 }, icon: "icon_tab_spaceship" },
  relic: { bar: { row: 0, col: 2 }, icon: "icon_tab_relic", feature: "relic" },
  codex: { bar: { row: 0, col: 1 }, icon: "icon_tab_codex" },
  shop: { bar: { row: 0, col: 0 }, icon: "icon_tab_shop" },
  rebirth: { bar: { row: 1, col: 4 }, icon: "icon_tab_rebirth" },
  moduleMerge: { bar: { row: 1, col: 3 }, icon: "icon_tab_merge" },
  moduleMastery: { bar: { row: 0, col: 3 }, icon: "icon_tab_mastery" },
  record: { bar: { row: 0, col: 4 }, icon: "icon_tab_record" },
  world: { bar: { row: 0, col: 5 }, icon: "icon_tab_world" },
} as const satisfies Record<string, TabCatalogEntry>;

export type TabId = keyof typeof CATALOG;

/** 카탈로그 — 항목 접근은 공통 형태(TabCatalogEntry)로 (feature 옵션 필드 union 접근 오류 방지). */
export const TAB_CATALOG: Readonly<Record<TabId, TabCatalogEntry>> = CATALOG;

/** 전체 탭 집합 — 가시성 SSoT(SingleSelection)에 전달. 카탈로그 키 선언 순서가 곧 순서. */
export const TAB_ORDER: readonly TabId[] = Object.keys(CATALOG) as TabId[];

/** 하단 탭바 행 배치 파생 — row 오름차순 → 행 내 col 오름차순 (BottomTabView 소비). */
export function deriveTabRows(): readonly (readonly TabId[])[] {
  const rows = [...new Set(TAB_ORDER.map((id) => TAB_CATALOG[id].bar.row))].sort((a, b) => a - b);
  return rows.map((row) =>
    TAB_ORDER.filter((id) => TAB_CATALOG[id].bar.row === row).sort(
      (a, b) => TAB_CATALOG[a].bar.col - TAB_CATALOG[b].bar.col,
    ),
  );
}

/** 탭 id → 아이콘 텍스처 키. */
export function tabIconKey(id: TabId): string {
  return TAB_CATALOG[id].icon;
}

/** 탭 id → 비활성 모노크롬 아이콘 텍스처 키. */
export function tabInactiveIconKey(id: TabId): string {
  return `${tabIconKey(id)}_inactive`;
}
